import {
  formatBRL, type PriceItem, type QuotableEvaluation, type Quote, type QuoteItemInput, type QuoteVersionInput,
} from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Dialog, Field, Input, Select, Spinner, cn } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Ban, Check, CheckCircle2, FileSignature, Pencil, Plus, Send, ShieldAlert, ShieldCheck, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { fieldErrors } from '../clinical/patients-page.js';
import { quoteSignaturesQuery, SignatureViewer, when as sigWhen } from '../documents/shared.js';
import { QuoteCharges } from './charges.js';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';
import {
  billingSettingsQuery, brDate, centsToInput, parseBRL, pct, priceItemsQuery, QUOTE_STATUS, quotableQuery, quoteQuery, UNIT_SHORT,
} from './queries.js';

interface Row { key: number; priceItemId: string | null; description: string; price: string; quantity: number }
let seq = 0;

function usePerms() {
  const { data: me } = useSuspenseQuery(meQuery);
  const p = new Set(me.permissions);
  return { free: p.has('billing.price.manage') || p.has('billing.discount.approve'), approve: p.has('billing.discount.approve') };
}

function EvaluationBox({ doctor, signedAt, min, max, technique }: { doctor: string; signedAt: string; min: number | null; max: number | null; technique: string | null }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-surface-muted/50 px-3.5 py-2.5 text-[13px] text-fg-muted">
      <span className="inline-flex items-center gap-1.5 text-fg"><ShieldCheck className="size-4 text-success" />Avaliação assinada por {doctor}</span>
      <span>{new Date(signedAt).toLocaleDateString('pt-BR')}</span>
      {min != null && <span>Estimativa: <strong className="text-fg">{min}{max ? `–${max}` : ''} UFs</strong></span>}
      {technique && <span>Técnica: {technique}</span>}
    </div>
  );
}

// ------------------------------------------------------------------ editor (cria orçamento ou nova versão)
function Editor({ initial, prices, onSubmit, submitting, error, onCancel, suggestedGrafts, submitLabel }: {
  initial: { rows: Row[]; discount: number; discountReason: string; maxInstallments: number; cashDiscountBp: number; validUntil: string; notes: string };
  prices: PriceItem[]; onSubmit: (v: QuoteVersionInput) => void; submitting: boolean; error: Error | null; onCancel?: () => void;
  suggestedGrafts: number | null; submitLabel: string;
}) {
  const { free } = usePerms();
  const { data: settings } = useQuery(billingSettingsQuery);
  const [rows, setRows] = useState<Row[]>(initial.rows);
  const [discountMode, setDiscountMode] = useState<'brl' | 'pct'>('brl');
  const [discountInput, setDiscountInput] = useState(initial.discount ? centsToInput(initial.discount) : '');
  const [reason, setReason] = useState(initial.discountReason);
  const [maxInst, setMaxInst] = useState(initial.maxInstallments);
  const [cashBp, setCashBp] = useState(String(initial.cashDiscountBp / 100));
  const [validUntil, setValidUntil] = useState(initial.validUntil);
  const [notes, setNotes] = useState(initial.notes);
  const active = prices.filter((p) => p.active);

  const lineTotal = (r: Row) => {
    const p = r.priceItemId ? prices.find((x) => x.id === r.priceItemId) : null;
    const unit = p ? p.priceCents : parseBRL(r.price) ?? 0;
    return unit * (p?.unit === 'fixed' ? 1 : r.quantity);
  };
  const subtotal = rows.reduce((t, r) => t + lineTotal(r), 0);
  const discount = discountMode === 'brl' ? parseBRL(discountInput) ?? 0 : Math.round((subtotal * Number(discountInput.replace(',', '.') || 0)) / 100);
  const bp = subtotal ? Math.round((discount * 10000) / subtotal) : 0;
  const total = Math.max(0, subtotal - discount);
  const overLimit = settings ? bp > settings.maxDiscountBp : false;
  const cash = total - Math.round((total * Math.round(Number(cashBp.replace(',', '.') || 0) * 100)) / 10000);

  const addRow = (priceItemId: string | null) => {
    const p = prices.find((x) => x.id === priceItemId);
    setRows([...rows, { key: ++seq, priceItemId, description: '', price: '', quantity: p?.unit === 'per_graft' ? suggestedGrafts ?? 1 : 1 }]);
  };
  const update = (key: number, patch: Partial<Row>) => setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const valid = rows.length > 0 && rows.every((r) => r.priceItemId || (r.description.trim().length >= 2 && parseBRL(r.price) != null)) && discount <= subtotal && (!overLimit || reason.trim());
  const errs = fieldErrors(error);

  const submit = () => onSubmit({
    items: rows.map((r): QuoteItemInput => (r.priceItemId ? { priceItemId: r.priceItemId, quantity: r.quantity } : { description: r.description.trim(), unitPriceCents: parseBRL(r.price) ?? 0, quantity: r.quantity })),
    discountCents: discount, discountReason: reason.trim() || null, maxInstallments: maxInst,
    cashDiscountBp: Math.round(Number(cashBp.replace(',', '.') || 0) * 100), validUntil, notes: notes.trim() || null,
  });

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="grid content-start gap-5">
        <Card>
          <CardHeader title="Itens" description="O preço vem da tabela da clínica." />
          <ul className="divide-y divide-border">
            {rows.map((r) => {
              const p = r.priceItemId ? prices.find((x) => x.id === r.priceItemId) : null;
              return (
                <li key={r.key} className="grid gap-3 px-5 py-3 sm:grid-cols-[minmax(0,1fr)_110px_130px_36px] sm:items-center">
                  {p ? (
                    <div className="min-w-0"><p className="truncate font-medium text-fg">{p.name}</p><p className="text-[12.5px] text-fg-muted">{formatBRL(p.priceCents)}{UNIT_SHORT[p.unit]}</p></div>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-[1fr_120px]">
                      <Input aria-label="Descrição do item avulso" value={r.description} onChange={(e) => update(r.key, { description: e.target.value })} placeholder="Item avulso" maxLength={120} />
                      <Input aria-label="Preço do item avulso" value={r.price} onChange={(e) => update(r.key, { price: e.target.value })} placeholder="R$ 0,00" inputMode="decimal" />
                    </div>
                  )}
                  {p?.unit === 'fixed' ? <span className="text-[13px] text-fg-subtle sm:text-center">—</span> : (
                    <Input aria-label={`Quantidade ${p?.name ?? r.description}`} type="number" min={1} max={10000} step={p?.unit === 'per_graft' ? 50 : 1} value={r.quantity}
                      onChange={(e) => update(r.key, { quantity: Math.max(1, Number(e.target.value) || 1) })} />
                  )}
                  <span className="font-semibold text-fg tabular-nums sm:text-right">{formatBRL(lineTotal(r))}</span>
                  <Button size="icon" variant="ghost" aria-label="Remover item" onClick={() => setRows(rows.filter((x) => x.key !== r.key))}><Trash2 className="size-4" /></Button>
                </li>
              );
            })}
            {!rows.length && <li className="px-5 py-6 text-center text-sm text-fg-muted">Adicione os itens do plano.</li>}
          </ul>
          <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-3">
            <Select aria-label="Adicionar item da tabela" value="" onChange={(e) => { if (e.target.value) addRow(e.target.value); }} className="w-72">
              <option value="">+ Item da tabela…</option>
              {active.map((p) => <option key={p.id} value={p.id}>{p.name} — {formatBRL(p.priceCents)}{UNIT_SHORT[p.unit]}</option>)}
            </Select>
            {free && <Button size="sm" variant="ghost" onClick={() => addRow(null)}><Plus className="size-4" />Item avulso</Button>}
            {!active.length && <span className="text-[13px] text-fg-muted">A tabela de preços está vazia (Configurações › Preços).</span>}
          </div>
        </Card>
        <Card>
          <CardHeader title="Condições" />
          <div className="grid gap-4 p-5 sm:grid-cols-3">
            <Field label="Parcelas no cartão (até)"><Input type="number" min={1} max={24} value={maxInst} onChange={(e) => setMaxInst(Math.min(24, Math.max(1, Number(e.target.value) || 1)))} /></Field>
            <Field label="Desconto à vista no Pix (%)"><Input value={cashBp} onChange={(e) => setCashBp(e.target.value)} inputMode="decimal" /></Field>
            <Field label="Válido até" error={errs.validUntil}><Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></Field>
            <div className="sm:col-span-3">
              <Field label="Observações para o paciente">
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={2000}
                  className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg focus:border-primary" />
              </Field>
            </div>
          </div>
        </Card>
      </div>
      <Card className="h-fit lg:sticky lg:top-4">
        <CardHeader title="Resumo" />
        <div className="grid gap-3 p-5 text-sm">
          <div className="flex justify-between text-fg-muted"><span>Subtotal</span><span className="tabular-nums">{formatBRL(subtotal)}</span></div>
          <Field label="Desconto" error={errs.discountCents} labelAction={
            <div className="flex rounded-md border border-border-strong p-0.5 text-[12px]" role="radiogroup" aria-label="Tipo de desconto">
              {(['brl', 'pct'] as const).map((m) => (
                <button key={m} type="button" role="radio" aria-checked={discountMode === m} onClick={() => { setDiscountMode(m); setDiscountInput(''); }}
                  className={cn('rounded px-2', discountMode === m ? 'bg-surface-muted font-medium text-fg' : 'text-fg-muted')}>{m === 'brl' ? 'R$' : '%'}</button>
              ))}
            </div>
          }>
            <Input value={discountInput} onChange={(e) => setDiscountInput(e.target.value)} inputMode="decimal" placeholder={discountMode === 'brl' ? '0,00' : '0'} />
          </Field>
          {discount > 0 && <p className={cn('-mt-1 text-[12.5px]', overLimit ? 'text-warning' : 'text-fg-muted')}>
            −{formatBRL(discount)} ({pct(bp)}){overLimit && settings ? ` · acima de ${pct(settings.maxDiscountBp)}: precisa de aprovação` : ''}
          </p>}
          {discount > subtotal && <p className="text-[12.5px] text-danger">O desconto passa do subtotal.</p>}
          {overLimit && (
            <Field label="Motivo do desconto" error={errs.discountReason}>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={280} placeholder="Ex.: indicação de paciente" />
            </Field>
          )}
          <div className="mt-1 flex items-baseline justify-between border-t border-border pt-3"><span className="font-medium text-fg">Total</span><span className="text-xl font-semibold text-fg tabular-nums">{formatBRL(total)}</span></div>
          {cash < total && <div className="flex justify-between text-fg-muted"><span>À vista no Pix</span><span className="tabular-nums">{formatBRL(cash)}</span></div>}
          {maxInst > 1 && <div className="flex justify-between text-fg-muted"><span>Cartão</span><span className="tabular-nums">até {maxInst}x de {formatBRL(Math.ceil(total / maxInst))}</span></div>}
          {error && !Object.keys(errs).length && <Alert tone="danger">{error.message}</Alert>}
          <div className="mt-2 flex gap-2">
            <Button className="flex-1" onClick={submit} disabled={!valid} loading={submitting}>{submitLabel}</Button>
            {onCancel && <Button variant="ghost" onClick={onCancel}>Cancelar</Button>}
          </div>
        </div>
      </Card>
    </div>
  );
}

const today = () => new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);
const plusDays = (d: number) => new Date(Date.now() - 3 * 3_600_000 + d * 86_400_000).toISOString().slice(0, 10);

/** Novo orçamento a partir de uma avaliação assinada. */
export function NewQuotePage({ evaluationId }: { evaluationId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: prices } = useQuery(priceItemsQuery);
  const { data: settings } = useQuery(billingSettingsQuery);
  const { data: quotable } = useQuery(quotableQuery);
  const ev: QuotableEvaluation | undefined = quotable?.find((e) => e.evaluationId === evaluationId);
  const create = useMutation({
    mutationFn: (v: QuoteVersionInput) => api<Quote>('/quotes', { method: 'POST', body: { ...v, evaluationId } }),
    onSuccess: (q) => { void qc.invalidateQueries({ queryKey: ['quotes'] }); void navigate({ to: '/orcamentos/$id', params: { id: q.id }, replace: true }); },
  });
  const initialRows = useMemo<Row[]>(() => {
    const graft = prices?.find((p) => p.active && p.unit === 'per_graft');
    return graft ? [{ key: ++seq, priceItemId: graft.id, description: '', price: '', quantity: ev?.graftsEstimateMax ?? ev?.graftsEstimateMin ?? 1 }] : [];
  }, [prices, ev]);
  if (!prices || !settings || !quotable) return <Spinner className="mt-10 size-5 text-fg-subtle" label="Carregando" />;
  if (!ev) return <Alert tone="danger" className="mx-auto max-w-xl">Avaliação não encontrada ou ainda não assinada.</Alert>;
  return (
    <div className="mx-auto max-w-6xl">
      <Link to="/orcamentos" className="inline-flex items-center gap-1.5 text-[13px] text-fg-muted hover:text-fg"><ArrowLeft className="size-3.5" />Orçamentos</Link>
      <h1 className="mt-3 text-[1.9rem] leading-tight text-fg sm:text-[2.2rem]">Orçamento para {ev.patientName}</h1>
      <div className="mt-4"><EvaluationBox doctor={ev.doctorName} signedAt={ev.signedAt} min={ev.graftsEstimateMin} max={ev.graftsEstimateMax} technique={ev.technique} /></div>
      <div className="mt-6">
        <Editor key={initialRows.length} prices={prices} suggestedGrafts={ev.graftsEstimateMax ?? ev.graftsEstimateMin} submitLabel="Criar orçamento"
          initial={{ rows: initialRows, discount: 0, discountReason: '', maxInstallments: settings.maxInstallments, cashDiscountBp: settings.cashDiscountBp, validUntil: plusDays(settings.validityDays), notes: '' }}
          onSubmit={(v) => create.mutate(v)} submitting={create.isPending} error={create.error} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ orçamento existente
function Actions({ q }: { q: Quote }) {
  const qc = useQueryClient();
  const { approve } = usePerms();
  const [dialog, setDialog] = useState<'send' | 'accept' | 'cancel' | null>(null);
  const [whatsapp, setWhatsapp] = useState(true);
  const [reason, setReason] = useState('');
  const [decisionNote, setDecisionNote] = useState('');
  const done = (n: Quote) => { qc.setQueryData(['quotes', q.id], n); void qc.invalidateQueries({ queryKey: ['quotes'] }); setDialog(null); };
  const send = useMutation({ mutationFn: () => api<Quote>(`/quotes/${q.id}/send`, { method: 'POST', body: { whatsapp } }), onSuccess: done });
  const accept = useMutation({ mutationFn: () => api<Quote>(`/quotes/${q.id}/accept`, { method: 'POST' }), onSuccess: done });
  const cancel = useMutation({ mutationFn: () => api<Quote>(`/quotes/${q.id}/cancel`, { method: 'POST', body: { reason } }), onSuccess: done });
  const decide = useMutation({ mutationFn: (decision: 'approved' | 'rejected') => api<Quote>(`/quotes/${q.id}/discount-decision`, { method: 'POST', body: { decision, reason: decisionNote || null } }), onSuccess: done });
  const { data: sigs } = useQuery(quoteSignaturesQuery(q.id));
  const [viewSig, setViewSig] = useState<string | null>(null);
  const askSign = useMutation({
    mutationFn: () => api<unknown>(`/quotes/${q.id}/signatures`, { method: 'POST', body: { notify: true } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['signatures', 'quote', q.id] }),
  });
  const pendingSig = sigs?.find((x) => x.status === 'pending' && x.quoteVersion === q.sentVersion);
  const signedSig = sigs?.find((x) => x.status === 'signed');
  const err = send.error ?? accept.error ?? cancel.error ?? decide.error ?? askSign.error;
  const v = q.current;
  return (
    <div className="grid gap-3">
      {v.discount.state === 'pending' && (
        <Alert tone="warning" title={`Desconto de ${pct(v.discountBp)} aguardando aprovação`}>
          <p>{v.discountReason}</p>
          {q.can.decideDiscount && approve && (
            <div className="mt-2.5 grid gap-2">
              <Input aria-label="Observação da decisão" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} placeholder="Observação (opcional)" />
              <div className="flex gap-2">
                <Button size="sm" loading={decide.isPending && decide.variables === 'approved'} onClick={() => decide.mutate('approved')}><Check className="size-4" />Aprovar desconto</Button>
                <Button size="sm" variant="secondary" loading={decide.isPending && decide.variables === 'rejected'} onClick={() => decide.mutate('rejected')}><X className="size-4" />Recusar</Button>
              </div>
            </div>
          )}
        </Alert>
      )}
      {v.discount.state === 'approved' && <p className="flex items-center gap-1.5 text-[13px] text-fg-muted"><ShieldCheck className="size-4 text-success" />Desconto aprovado por {v.discount.decidedByName}</p>}
      {v.discount.state === 'rejected' && <Alert tone="danger" title="Desconto recusado">{v.discount.decidedByName}{v.discount.reason ? `: ${v.discount.reason}` : ''}. Ajuste o orçamento.</Alert>}
      {q.status === 'expired' && <Alert tone="warning">A validade venceu em {brDate(v.validUntil)}. Para reaproveitar, altere o orçamento (nova validade) e envie de novo.</Alert>}
      {signedSig && (
        <button type="button" onClick={() => setViewSig(signedSig.id)} className="flex items-center gap-1.5 text-left text-[13px] text-fg-muted hover:text-fg">
          <ShieldCheck className="size-4 text-success" />Assinado pelo paciente em {signedSig.signedAt && sigWhen(signedSig.signedAt)} · ver manifesto
        </button>
      )}
      {pendingSig && (
        <button type="button" onClick={() => setViewSig(pendingSig.id)} className="flex items-center gap-1.5 text-left text-[13px] text-fg-muted hover:text-fg">
          <FileSignature className="size-4 text-warning" />Aguardando a assinatura do paciente (enviado {sigWhen(pendingSig.createdAt)})
        </button>
      )}
      <div className="flex flex-wrap gap-2">
        {q.can.send && <Button onClick={() => setDialog('send')}><Send className="size-4" />Enviar ao paciente</Button>}
        {q.can.accept && !pendingSig && <Button onClick={() => askSign.mutate()} loading={askSign.isPending}><FileSignature className="size-4" />Pedir assinatura do paciente</Button>}
        {q.can.accept && <Button variant="secondary" onClick={() => setDialog('accept')}><CheckCircle2 className="size-4" />Marcar como aceito</Button>}
        {q.can.cancel && <Button variant="ghost" onClick={() => setDialog('cancel')}><Ban className="size-4" />Cancelar</Button>}
      </div>
      {err && <Alert tone="danger">{err.message}</Alert>}
      {viewSig && <SignatureViewer id={viewSig} onClose={() => setViewSig(null)} />}
      <Dialog open={dialog === 'send'} onClose={() => setDialog(null)} title={`Enviar ${q.code}?`} description={`${formatBRL(v.totalCents)} · válido até ${brDate(v.validUntil)}`}>
        <div className="grid gap-4 p-5 text-sm text-fg-muted">
          <label className="flex items-start gap-2.5 text-fg"><input type="checkbox" className="mt-0.5 size-4 accent-[var(--dnx-primary)]" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />Mandar o resumo pelo WhatsApp (itens, total, à vista e parcelado)</label>
          <p>O lead passa para <strong className="text-fg">Orçado</strong>. Depois de enviado, qualquer alteração vira uma nova versão e precisa ser reenviada.</p>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>Voltar</Button><Button loading={send.isPending} onClick={() => send.mutate()}><Send className="size-4" />Enviar</Button></div>
        </div>
      </Dialog>
      <Dialog open={dialog === 'accept'} onClose={() => setDialog(null)} title="Paciente aceitou?" description={`Versão ${q.sentVersion} · ${formatBRL(v.totalCents)}`}>
        <div className="grid gap-4 p-5 text-sm text-fg-muted">
          <p>Use quando o paciente aceitou pessoalmente, sem assinar pela área do paciente. O orçamento fica fechado (não muda mais) e o lead vai para <strong className="text-fg">Ganho</strong>.</p>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>Voltar</Button><Button loading={accept.isPending} onClick={() => accept.mutate()}><CheckCircle2 className="size-4" />Confirmar aceite</Button></div>
        </div>
      </Dialog>
      <Dialog open={dialog === 'cancel'} onClose={() => setDialog(null)} title={`Cancelar ${q.code}?`}>
        <form className="grid gap-4 p-5" onSubmit={(e) => { e.preventDefault(); cancel.mutate(); }}>
          <Field label="Motivo"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={280} autoFocus placeholder="Ex.: paciente desistiu, preço" /></Field>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>Voltar</Button><Button type="submit" variant="danger" disabled={reason.trim().length < 3} loading={cancel.isPending}>Cancelar orçamento</Button></div>
        </form>
      </Dialog>
    </div>
  );
}

function Document({ q }: { q: Quote }) {
  const v = q.current;
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <p className="text-[12px] font-semibold tracking-[0.14em] text-fg-subtle uppercase">Orçamento {q.code} · versão {v.version}</p>
          <p className="mt-1 text-[13px] text-fg-muted">Feito por {v.createdByName} em {new Date(v.createdAt).toLocaleDateString('pt-BR')} · válido até {brDate(v.validUntil)}</p>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[12px] text-fg-subtle"><th className="px-5 py-2 font-medium">Item</th><th className="px-3 py-2 text-right font-medium">Qtd.</th><th className="px-3 py-2 text-right font-medium">Unitário</th><th className="px-5 py-2 text-right font-medium">Total</th></tr></thead>
        <tbody className="divide-y divide-border border-y border-border">
          {v.items.map((i, n) => (
            <tr key={n}><td className="px-5 py-2.5 text-fg">{i.description}</td><td className="px-3 py-2.5 text-right tabular-nums text-fg-muted">{i.unit === 'fixed' && i.quantity === 1 ? '—' : i.quantity}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-fg-muted">{formatBRL(i.unitPriceCents)}</td><td className="px-5 py-2.5 text-right font-medium tabular-nums text-fg">{formatBRL(i.totalCents)}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="ml-auto grid max-w-sm gap-1.5 px-5 py-4 text-sm">
        <div className="flex justify-between text-fg-muted"><span>Subtotal</span><span className="tabular-nums">{formatBRL(v.subtotalCents)}</span></div>
        {v.discountCents > 0 && <div className="flex justify-between text-fg-muted"><span>Desconto ({pct(v.discountBp)})</span><span className="tabular-nums">−{formatBRL(v.discountCents)}</span></div>}
        <div className="flex items-baseline justify-between border-t border-border pt-2"><span className="font-medium text-fg">Total</span><span className="text-xl font-semibold tabular-nums text-fg">{formatBRL(v.totalCents)}</span></div>
        {v.cashDiscountBp > 0 && <div className="flex justify-between text-fg-muted"><span>À vista no Pix ({pct(v.cashDiscountBp)} off)</span><span className="tabular-nums">{formatBRL(v.cashTotalCents)}</span></div>}
        {v.maxInstallments > 1 && <div className="flex justify-between text-fg-muted"><span>Cartão</span><span className="tabular-nums">até {v.maxInstallments}x de {formatBRL(Math.ceil(v.totalCents / v.maxInstallments))}</span></div>}
      </div>
      {(v.notes || q.terms) && (
        <div className="grid gap-2 border-t border-border px-5 py-4 text-[13px] text-fg-muted">
          {v.notes && <p className="whitespace-pre-wrap text-fg">{v.notes}</p>}
          {q.terms && <p className="whitespace-pre-wrap">{q.terms}</p>}
        </div>
      )}
    </Card>
  );
}

export function QuotePage({ id }: { id: string }) {
  const qc = useQueryClient();
  const { data: q, error } = useQuery(quoteQuery(id));
  const { data: prices } = useQuery(priceItemsQuery);
  const [editing, setEditing] = useState(false);
  const revise = useMutation({
    mutationFn: (v: QuoteVersionInput) => api<Quote>(`/quotes/${id}/versions`, { method: 'POST', body: v }),
    onSuccess: (n) => { qc.setQueryData(['quotes', id], n); void qc.invalidateQueries({ queryKey: ['quotes'] }); setEditing(false); },
  });
  if (error) return <Alert tone="danger" className="mx-auto max-w-xl">{error.message}</Alert>;
  if (!q || !prices) return <Spinner className="mt-10 size-5 text-fg-subtle" label="Carregando" />;
  const v = q.current;
  return (
    <div className="mx-auto max-w-6xl">
      <Link to="/orcamentos" className="inline-flex items-center gap-1.5 text-[13px] text-fg-muted hover:text-fg"><ArrowLeft className="size-3.5" />Orçamentos</Link>
      <header className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex flex-wrap items-center gap-3 text-[1.9rem] leading-tight text-fg sm:text-[2.2rem]">{q.patientName}
            <Badge tone={QUOTE_STATUS[q.status][1]}>{QUOTE_STATUS[q.status][0]}</Badge>
            {q.discountPending && <Badge tone="warning"><ShieldAlert className="size-3" />Desconto aguardando</Badge>}</h1>
          <p className="mt-1.5 text-sm text-fg-muted"><span className="font-mono">{q.code}</span> · versão {v.version}{q.sentVersion ? ` · enviada a v${q.sentVersion}` : ''}{q.cancelReason ? ` · cancelado: ${q.cancelReason}` : ''}</p>
        </div>
        {q.can.edit && !editing && <Button variant="secondary" onClick={() => setEditing(true)}><Pencil className="size-4" />Alterar (nova versão)</Button>}
      </header>
      <div className="mt-4"><EvaluationBox doctor={q.evaluation.doctorName} signedAt={q.evaluation.signedAt} min={q.evaluation.graftsEstimateMin} max={q.evaluation.graftsEstimateMax} technique={q.evaluation.technique} /></div>
      <div className="mt-6">
        {editing ? (
          <Editor prices={prices} suggestedGrafts={q.evaluation.graftsEstimateMax} submitLabel={`Salvar versão ${v.version + 1}`} onCancel={() => setEditing(false)}
            initial={{
              rows: v.items.map((i) => ({ key: ++seq, priceItemId: i.priceItemId, description: i.priceItemId ? '' : i.description, price: i.priceItemId ? '' : centsToInput(i.unitPriceCents), quantity: i.quantity })),
              discount: v.discountCents, discountReason: v.discountReason ?? '', maxInstallments: v.maxInstallments, cashDiscountBp: v.cashDiscountBp,
              validUntil: v.validUntil < today() ? plusDays(15) : v.validUntil, notes: v.notes ?? '',
            }}
            onSubmit={(x) => revise.mutate(x)} submitting={revise.isPending} error={revise.error} />
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <Document q={q} />
            <div className="grid content-start gap-5">
              <Card className="p-5"><Actions q={q} /></Card>
              <QuoteCharges q={q} />
              <Card>
                <CardHeader title="Versões" />
                <ol className="divide-y divide-border">
                  {q.versions.map((x) => (
                    <li key={x.version} className="flex items-center justify-between gap-3 px-5 py-2.5 text-[13px]">
                      <span className="text-fg">v{x.version}{x.version === q.sentVersion && <span className="text-fg-muted"> · enviada</span>}{x.version === q.acceptedVersion && <span className="text-success"> · aceita</span>}
                        <span className="block text-[12px] text-fg-muted">{x.createdByName} · {new Date(x.createdAt).toLocaleDateString('pt-BR')}</span></span>
                      <span className="tabular-nums text-fg">{formatBRL(x.totalCents)}</span>
                    </li>
                  ))}
                </ol>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
